import { APP_ROUTES, SUCCESS_MESSAGES } from '@zorro/common-utils';
import { Badge } from '@zorro/design-system/v2/Badge';
import { Tabs } from '@zorro/design-system/v2/Tabs';
import { Title } from '@zorro/design-system/v2/Title';
import { Group, Stack } from '@zorro/web/design-system';
import {
  showSuccessNotification,
  useAppRouter,
  useLoadingOverlay,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { ReactElement } from 'react';
import { FieldValues, FormState } from 'react-hook-form';

import { EmployerDotsMenu } from '../EmployerDotsMenu';
import { FormFooter } from '../FormFooter';

export enum CompanySetupTabType {
  COMPANY_DETAILS = 'COMPANY_DETAILS',
  CONTACTS = 'CONTACTS',
  COMPANY_SETUP = 'COMPANY_SETUP',
  ZORRO_SETUP = 'ZORRO_SETUP',
  PLAN_YEAR_SETUP = 'PLAN_YEAR_SETUP',
  HR_SYSTEM_SYNC = 'HR_SYSTEM_SYNC',
}

const getContentWidth = (isPayrollCycleVisible = false) => {
  return isPayrollCycleVisible ? '790px' : '710px';
};

const getSetupTabConfig = (
  isAppMigration: boolean
): Record<
  CompanySetupTabType,
  {
    label: string;
    route: keyof typeof APP_ROUTES;
    isFullPageTab?: boolean;
  }
> => ({
  [CompanySetupTabType.COMPANY_DETAILS]: {
    label: 'Company details',
    route: isAppMigration
      ? 'DON_COMPANY_SETUP_COMPANY_DETAILS'
      : 'OPS_COMPANY_SETUP_COMPANY_DETAILS',
  },
  [CompanySetupTabType.CONTACTS]: {
    label: 'Contacts',
    route: isAppMigration
      ? 'DON_COMPANY_SETUP_CONTACTS'
      : 'OPS_COMPANY_SETUP_CONTACTS',
  },
  [CompanySetupTabType.COMPANY_SETUP]: {
    label: 'Company setup',
    route: isAppMigration
      ? 'DON_COMPANY_SETUP_COMPANY_SETUP'
      : 'OPS_COMPANY_SETUP_COMPANY_SETUP',
  },
  [CompanySetupTabType.ZORRO_SETUP]: {
    label: 'Zorro setup',
    route: 'OPS_COMPANY_SETUP_ZORRO_SETUP',
  },
  [CompanySetupTabType.PLAN_YEAR_SETUP]: {
    label: 'Plan year setup',
    route: isAppMigration
      ? 'DON_COMPANY_SETUP_PLAN_YEAR_SETUP'
      : 'OPS_COMPANY_SETUP_PLAN_YEAR_SETUP',
    isFullPageTab: true,
  },
  [CompanySetupTabType.HR_SYSTEM_SYNC]: {
    label: 'HR system sync',
    route: isAppMigration
      ? 'DON_COMPANY_SETUP_PAYROLL_SYNC'
      : 'OPS_COMPANY_SETUP_PAYROLL_SYNC',
    isFullPageTab: true,
  },
});

function getSetupTabs(
  {
    isZorroOperations,
    isEmployerAdmin,
    isAgentWithoutSupervisor,
    isAccountSupervisor,
  }: ReturnType<typeof useRoles>,
  isHrSystemSyncOn: boolean = false
) {
  const allSetupTabs = Object.values(CompanySetupTabType);

  return allSetupTabs.filter((setupTab) => {
    switch (setupTab) {
      case CompanySetupTabType.ZORRO_SETUP: {
        if (isEmployerAdmin || isAgentWithoutSupervisor) {
          return false;
        }
        break;
      }
      case CompanySetupTabType.HR_SYSTEM_SYNC: {
        if (!isHrSystemSyncOn) {
          return false;
        }
        if (!(isZorroOperations || isEmployerAdmin || isAccountSupervisor)) {
          return false;
        }
        break;
      }
    }

    return true;
  });
}

type Props = {
  tab: CompanySetupTabType;
  employerId: string;
  children: ReactElement;
  isDon: boolean;
  formState?: FormState<FieldValues>;
  onSubmit?: () => Promise<boolean>;
  onCancel?: () => void;
};

export const CompanySetupTabWrapper = ({
  tab,
  employerId,
  onSubmit,
  onCancel,
  formState,
  children,
  isDon,
}: Props) => {
  const rolesInfo = useRoles();
  const { isAgentWithoutSupervisor: isReadonlyMode, isEmployerAdmin } =
    rolesInfo;

  const { startLoading, stopLoading } = useLoadingOverlay();
  const appRouter = useAppRouter();

  const setupTabConfig = getSetupTabConfig(isDon);

  const { isFullPageTab } = setupTabConfig[tab];

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: [employerId],
  });

  const isHrSystemSyncOn = Boolean(
    employer?.isPayrollSyncOn || employer?.isEmploymentSyncOn
  );

  const contentWidth = getContentWidth(isHrSystemSyncOn);

  const handleTabClick = (newTab: CompanySetupTabType) => {
    const { route } = setupTabConfig[newTab];
    appRouter.navigateToRoute(route, [employerId]);
  };

  const handleSubmit = async () => {
    startLoading();
    if (await onSubmit?.()) {
      showSuccessNotification({
        message: SUCCESS_MESSAGES.SAVED_SUCCESSFULLY_MESSAGE,
      });
    }

    stopLoading();
  };

  const { isValid, isDirty } = formState || {};
  const isSubmitDisabled = !isValid || !isDirty;

  const showFooter = Boolean(formState && onSubmit && !isReadonlyMode);

  const setupTabs = getSetupTabs(rolesInfo, isHrSystemSyncOn);

  return (
    <Stack
      maw={isFullPageTab ? undefined : contentWidth}
      // Full-page tabs (e.g. Plan year setup) host a DataTable that must fill
      // the remaining height. Continue the flex-column chain from the layout
      // (AppLayout variant="fit-content") so descendants using grow can stretch.
      flex={isFullPageTab ? 1 : undefined}
      mih={isFullPageTab ? 0 : undefined}
      // Reserve space for the fixed FormFooter (position: fixed at the viewport
      // bottom) so the last content is not hidden behind the "Save changes" bar.
      pb={showFooter ? '6rem' : undefined}
    >
      <Group gap="xs">
        <Title>Setup</Title>
        {!isReadonlyMode && !isEmployerAdmin && (
          <EmployerDotsMenu employerId={employerId} />
        )}
        {employer?.isDemo && (
          <Badge
            color="indigo"
            style={{ marginLeft: 'var(--mantine-spacing-sm)' }}
          >
            Demo company
          </Badge>
        )}
      </Group>
      <Tabs maw={contentWidth} mb="xl" keepMounted={false} value={tab}>
        <Tabs.List grow>
          {setupTabs.map((newTab) => (
            <Tabs.Tab
              onClick={() => handleTabClick(newTab)}
              value={newTab}
              key={newTab}
            >
              {setupTabConfig[newTab].label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
      {children}
      {showFooter && (
        <FormFooter
          primaryLabel="Save changes"
          primaryButtonProps={{
            disabled: isSubmitDisabled,
            onClick: handleSubmit,
          }}
          secondaryLabel={onCancel && isDirty ? 'Cancel' : undefined}
          secondaryButtonProps={{ onClick: onCancel }}
          // Offset the fixed footer by the sidebar/menu width so it only spans
          // the content area, not the menu. --app-shell-navbar-offset is set by
          // Mantine's AppShell (0 when the menu is collapsed).
          paperProps={{
            left: 'var(--app-shell-navbar-offset, 0)',
            // eslint-disable-next-line id-length -- Mantine size prop
            w: 'auto',
          }}
        />
      )}
    </Stack>
  );
};
